
import * as React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { getCachedImageUrl } from "@/lib/imageCache";

interface StableAvatarProps {
  avatarUrl: string | null;
  fallbackText: string;
  className?: string;
  size?: "sm" | "md" | "lg" | "xl";
  isLoaded?: boolean;
  onLoad?: () => void;
  updatedAt?: string; // Optional timestamp for cache-busting
}

const sizeClasses = {
  sm: "h-8 w-8",
  md: "h-10 w-10", 
  lg: "h-16 w-16",
  xl: "h-24 w-24 md:h-32 md:w-32"
};

const MAX_RETRIES = 3;
const RETRY_DELAYS = [500, 1500, 3000]; // Increasing delays

const StableAvatarComponent = React.forwardRef<
  HTMLDivElement,
  StableAvatarProps
>(({ 
  avatarUrl, 
  fallbackText, 
  className,
  size = "md",
  isLoaded = true,
  onLoad,
  updatedAt
}, ref) => {
  const [retryCount, setRetryCount] = React.useState(0);
  const [hasError, setHasError] = React.useState(false);
  const [isRetrying, setIsRetrying] = React.useState(false);
  const retryTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Only add cache-busting when updatedAt timestamp is provided or on retry
  const finalImageUrl = React.useMemo(() => {
    if (!avatarUrl) return null;
    
    let imageUrl = getCachedImageUrl(avatarUrl) || avatarUrl;
    
    // Add version parameter if updatedAt is provided OR on retry (cache-bust)
    const version = updatedAt 
      ? new Date(updatedAt).getTime() 
      : (retryCount > 0 ? Date.now() : null);
    
    if (version) {
      try {
        const url = new URL(imageUrl);
        url.searchParams.set('v', version.toString());
        imageUrl = url.toString();
      } catch (error) {
        // Fallback to simple cache busting
        const separator = imageUrl.includes('?') ? '&' : '?';
        imageUrl = `${imageUrl}${separator}v=${version}`;
      }
    }
    
    return imageUrl;
  }, [avatarUrl, updatedAt, retryCount]);

  const fallbackLetter = React.useMemo(() => {
    return fallbackText[0]?.toUpperCase() || "U";
  }, [fallbackText]);

  // remember last good image to avoid initial letter flash between renders
  const lastGoodImageRef = React.useRef<string | null>(null);
  
  // Reset retry state when URL changes
  React.useEffect(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    setRetryCount(0);
    setHasError(false);
    setIsRetrying(false);
  }, [avatarUrl]);

  React.useEffect(() => {
    if (finalImageUrl && !hasError) lastGoodImageRef.current = finalImageUrl;
  }, [finalImageUrl, hasError]);

  const displayImageUrl = lastGoodImageRef.current ?? finalImageUrl;

  const handleImageLoad = React.useCallback(() => {
    // Success - reset error state and store good URL
    setHasError(false);
    setIsRetrying(false);
    if (finalImageUrl) lastGoodImageRef.current = finalImageUrl;
    onLoad?.();
  }, [onLoad, finalImageUrl]);

  const handleImageError = React.useCallback(() => {
    if (retryCount < MAX_RETRIES) {
      // Schedule a retry with increasing delay
      setIsRetrying(true);
      const delay = RETRY_DELAYS[retryCount] || 3000;
      console.log(`[Avatar] Retry ${retryCount + 1}/${MAX_RETRIES} for ${avatarUrl} in ${delay}ms`);
      
      retryTimeoutRef.current = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        setIsRetrying(false);
      }, delay);
    } else {
      // Max retries reached - show fallback
      console.warn(`[Avatar] Failed to load after ${MAX_RETRIES} retries:`, avatarUrl);
      setHasError(true);
      setIsRetrying(false);
      onLoad?.();
    }
  }, [retryCount, avatarUrl, onLoad]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    // If no image URL, call onLoad immediately
    if (!displayImageUrl) {
      onLoad?.();
    }
  }, [displayImageUrl, onLoad]);

  return (
    <Avatar 
      ref={ref}
      className={cn(
        sizeClasses[size],
        "transition-opacity duration-200",
        className
      )}
    >
      {displayImageUrl && !hasError && (
        <AvatarImage 
          key={`${displayImageUrl}-${retryCount}`} // Force re-render on retry
          src={finalImageUrl || displayImageUrl} 
          alt={fallbackText}
          className="transition-opacity duration-200 object-cover"
          loading="eager"
          {...({ fetchpriority: "high" } as any)}
          decoding="sync"
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={{
            opacity: isRetrying ? 0.5 : 1
          }}
        />
      )}
      <AvatarFallback 
        className="bg-gradient-to-br from-blue-600 to-purple-600 text-white font-medium transition-opacity duration-200"
        delayMs={hasError ? 0 : 1000}
        style={{
          opacity: (!displayImageUrl || hasError) ? 1 : 0
        }}
      >
        {fallbackLetter}
      </AvatarFallback>
    </Avatar>
  );
});

StableAvatarComponent.displayName = "StableAvatar";

// Memoize the component to prevent unnecessary re-renders
export const StableAvatar = React.memo(StableAvatarComponent, (prevProps, nextProps) => {
  // Custom comparison to prevent re-renders when only non-essential props change
  return (
    prevProps.avatarUrl === nextProps.avatarUrl &&
    prevProps.fallbackText === nextProps.fallbackText &&
    prevProps.size === nextProps.size &&
    prevProps.isLoaded === nextProps.isLoaded &&
    prevProps.className === nextProps.className &&
    prevProps.onLoad === nextProps.onLoad &&
    prevProps.updatedAt === nextProps.updatedAt
  );
});
